import { cn } from '../utilitarios/cn'

export interface LinhaBarra {
  rotulo: string
  valor: number
  /** Texto à direita do rótulo (percentual, taxa). */
  anotacao?: string
  cor?: string
  onSelecionar?: () => void
}

export interface PropriedadesBarrasHorizontais {
  linhas: ReadonlyArray<LinhaBarra>
  /** Referência de 100%. Ausente = maior valor da série. */
  maximo?: number
  className?: string
  vazio?: string
}

/**
 * Barras horizontais — ranking por motivo de perda, por ponto de conversão, por
 * equipe.
 *
 * Horizontal e não vertical porque o rótulo é um nome ("Telefone somente para o
 * whatsapp"), e nome em eixo vertical vira texto girado. Uma cor só: a barra
 * codifica magnitude, não identidade — pintar cada linha de uma cor sugeriria que
 * a cor significa algo.
 */
export function BarrasHorizontais({
  linhas,
  maximo,
  className,
  vazio = 'Sem dados no período.',
}: PropriedadesBarrasHorizontais) {
  if (linhas.length === 0) {
    return <p className={cn('py-6 text-center text-sm text-conteudo-fraco', className)}>{vazio}</p>
  }

  const referencia = maximo ?? Math.max(...linhas.map((linha) => linha.valor), 1)

  return (
    <ul className={cn('flex flex-col gap-2.5', className)}>
      {linhas.map((linha) => {
        const proporcao = referencia > 0 ? (linha.valor / referencia) * 100 : 0
        const conteudo = (
          <>
            <div className="mb-1 flex items-baseline justify-between gap-3 text-xs">
              <span className="min-w-0 truncate text-conteudo-suave">{linha.rotulo}</span>
              <span className="shrink-0 tabular-nums text-conteudo">
                <span className="font-semibold">{linha.valor.toLocaleString('pt-BR')}</span>
                {linha.anotacao ? (
                  <span className="ml-1.5 text-conteudo-fraco">{linha.anotacao}</span>
                ) : null}
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-[4px] bg-superficie-3">
              <div
                className="h-full rounded-[4px]"
                style={{
                  width: `${Math.max(proporcao, linha.valor > 0 ? 1.5 : 0)}%`,
                  background: linha.cor ?? 'var(--serie-1)',
                }}
              />
            </div>
          </>
        )

        return (
          <li key={linha.rotulo}>
            {linha.onSelecionar ? (
              <button
                type="button"
                onClick={linha.onSelecionar}
                className="w-full rounded px-1 py-0.5 text-left hover:bg-superficie-2"
              >
                {conteudo}
              </button>
            ) : (
              <div className="px-1 py-0.5">{conteudo}</div>
            )}
          </li>
        )
      })}
    </ul>
  )
}

export interface EtapaFunilVisual {
  rotulo: string
  valor: number
  percentualDoTopo: number
  onSelecionar?: () => void
}

/**
 * Funil de conversão (`/conversoes/funil`).
 *
 * Rampa **ordinal** de um matiz só — etapas são ordenadas, não categorias. O
 * passo mais claro respeita o piso de 2:1 contra a superfície clara, senão a
 * primeira etapa desapareceria no fundo.
 *
 * As etapas **não somam** o total: `AGENDADOS` e `COM_PROPOSTA` significam
 * "existe agendamento/proposta para este lead" (`05-dashboard-metrics.md §6`), e
 * um lead que agendou, visitou e comprou conta nas três. O rótulo diz
 * "% do topo" justamente para não sugerir soma.
 */
export function Funil({ etapas, className }: { etapas: ReadonlyArray<EtapaFunilVisual>; className?: string }) {
  const RAMPA = ['#86b6ef', '#5598e7', '#3987e5', '#2a78d6', '#256abf', '#1c5cab', '#184f95']

  return (
    <ol className={cn('flex flex-col gap-2', className)}>
      {etapas.map((etapa, indice) => (
        <li key={etapa.rotulo}>
          <button
            type="button"
            disabled={!etapa.onSelecionar}
            onClick={etapa.onSelecionar}
            className={cn(
              'w-full rounded-md px-1 py-1 text-left',
              etapa.onSelecionar ? 'hover:bg-superficie-2' : 'cursor-default',
            )}
          >
            <div className="mb-1 flex items-baseline justify-between gap-3 text-xs">
              <span className="truncate text-conteudo-suave">{etapa.rotulo}</span>
              <span className="shrink-0 tabular-nums">
                <span className="font-semibold text-conteudo">
                  {etapa.valor.toLocaleString('pt-BR')}
                </span>
                <span className="ml-1.5 text-conteudo-fraco">
                  {etapa.percentualDoTopo.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}% do
                  topo
                </span>
              </span>
            </div>
            <div className="h-5 w-full overflow-hidden rounded-[4px] bg-superficie-3">
              <div
                className="h-full rounded-[4px] transition-[width]"
                style={{
                  width: `${Math.max(Math.min(etapa.percentualDoTopo, 100), etapa.valor > 0 ? 1.5 : 0)}%`,
                  background: RAMPA[Math.min(indice, RAMPA.length - 1)],
                }}
              />
            </div>
          </button>
        </li>
      ))}
    </ol>
  )
}
